import { useState } from 'react';

import { Check, Plus, Send, Trash2, Undo2 } from 'lucide-react';

import type { AtsCandidate } from '@/api/schema';
import { ApiError } from '@/api/client';
import { Alert } from '@/components/feedback/Alert';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { Drawer } from '@/components/ui/Drawer';
import { ErrorState } from '@/components/ui/EmptyState';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useCurrentUser } from '@/features/auth/api';
import { useDateFormat } from '@/features/company/api';
import { formatDate } from '@/lib/dates';
import { can } from '@/lib/permissions';

import { useAddReferral, useReferrals, useRemoveReferral, useShareReferral } from '../api';
import styles from './ReferralDrawer.module.css';

/**
 * The jobs one candidate has been shared to.
 *
 * A drawer rather than a page: it hangs off a row in a list somebody is working
 * through, and navigating away to add two jobs and come back would lose their place.
 */
export function ReferralDrawer({
  candidate,
  onClose,
}: {
  candidate: AtsCandidate;
  onClose: () => void;
}) {
  const { data: currentUser } = useCurrentUser();
  const dateFormat = useDateFormat();
  const referrals = useReferrals(candidate.id);
  const add = useAddReferral(candidate.id);
  const share = useShareReferral();
  const remove = useRemoveReferral();

  const [job, setJob] = useState('');
  const [country, setCountry] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const submit = async () => {
    if (!job.trim() || !country.trim()) {
      setProblem('Give both a job title and a country.');
      return;
    }
    setProblem(null);
    try {
      await add.mutateAsync({ jobTitle: job.trim(), country: country.trim() });
      setJob('');
      setCountry('');
    } catch (error) {
      // The duplicate case lands here, and the server's message names the job and the
      // country, worth showing as it is rather than replacing with something vaguer.
      setProblem(error instanceof ApiError ? error.message : 'That job was not added.');
    }
  };

  const rows = referrals.data ?? [];
  const shared = rows.filter((row) => row.shared).length;

  /**
   * Nothing loaded, as opposed to nothing to load.
   *
   * The copy below states as a fact that this candidate has no referrals, and a failed
   * request is no evidence of that: it contradicts the "2 of 5 shared" the user just
   * clicked, and sends them off re-entering jobs that are already there, which the
   * server then refuses as duplicates.
   *
   * `rows` is checked as well as the error because a refetch that fails after the list
   * has been shown once keeps the cached rows, and the list as last known beats an
   * error message where a list used to be.
   */
  const failed = referrals.isError && rows.length === 0;

  /**
   * DELETE on a referral needs ATS delete, which the seeded Staff role does not have:
   * it is granted view, create and edit only. Rendered unconditionally, the button is
   * a 403 every time it is pressed, on a row that never changes.
   */
  const canRemove = can(currentUser, 'ats', 'delete');

  return (
    <Drawer
      open
      onClose={onClose}
      title={candidate.full_name}
      description={`${candidate.reference} · referrals`}
    >
      {failed ? null : (
        <p className={styles.ratio}>
          {rows.length === 0 ? (
            'No jobs added yet.'
          ) : (
            <>
              <b>
                {shared} of {rows.length}
              </b>{' '}
              shared
            </>
          )}
        </p>
      )}

      <div className={styles.add}>
        <Field label="Job title">
          {({ id }) => (
            <Input
              id={id}
              value={job}
              placeholder="Chef"
              onChange={(event) => {
                setJob(event.target.value);
              }}
            />
          )}
        </Field>
        <Field label="Country">
          {({ id }) => (
            <Input
              id={id}
              value={country}
              placeholder="Malta"
              onChange={(event) => {
                setCountry(event.target.value);
              }}
            />
          )}
        </Field>
        <Button
          icon={<Plus size={15} />}
          loading={add.isPending}
          onClick={() => {
            void submit();
          }}
        >
          Add
        </Button>
      </div>

      {problem ? (
        <Alert tone="danger">
          <b>{problem}</b>
        </Alert>
      ) : null}

      {referrals.isPending ? (
        <p className={styles.empty}>
          <Spinner size={14} label="Loading referrals" />
        </p>
      ) : failed ? (
        <ErrorState
          message={
            referrals.error instanceof ApiError
              ? referrals.error.message
              : 'The jobs for this candidate did not load.'
          }
          onRetry={() => {
            void referrals.refetch();
          }}
        />
      ) : rows.length === 0 ? (
        <p className={styles.empty}>
          Add the jobs this candidate has been put forward for, and mark each one as it is
          shared.
        </p>
      ) : (
        <ul className={styles.list}>
          {rows.map((row) => (
            <li key={row.id} className={styles.row}>
              <div className={styles.job}>
                <span className={styles.jobTitle}>{row.job_title}</span>
                <span className={styles.country}>{row.country}</span>
              </div>

              {row.shared ? (
                <Chip size="sm" tone="approved">
                  <Check size={12} aria-hidden="true" />
                  {row.shared_on ? formatDate(row.shared_on, dateFormat) : 'Shared'}
                </Chip>
              ) : (
                <Chip size="sm" tone="neutral">
                  Not shared
                </Chip>
              )}

              <div className={styles.actions}>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={row.shared ? <Undo2 size={14} /> : <Send size={14} />}
                  aria-label={
                    row.shared
                      ? `Undo sharing ${row.job_title}`
                      : `Mark ${row.job_title} as shared`
                  }
                  onClick={() => {
                    share.mutate({ id: row.id, shared: !row.shared });
                  }}
                >
                  {row.shared ? 'Undo' : 'Mark shared'}
                </Button>
                {canRemove ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Trash2 size={14} />}
                    aria-label={`Remove ${row.job_title}`}
                    onClick={() => {
                      setProblem(null);
                      remove.mutate(row.id, {
                        // Without this the refusal is invisible: the row does not
                        // move, no toast is raised, and the only global handler
                        // reacts to a 401. Somebody presses it again, and again.
                        onError: (error) => {
                          setProblem(
                            error instanceof ApiError
                              ? error.message
                              : `${row.job_title} was not removed.`,
                          );
                        },
                      });
                    }}
                  />
                ) : null}
              </div>

              {row.sharer ? (
                <span className={styles.by}>Shared by {row.sharer.full_name}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
}
